import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeIn = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const halfSec = { duration: 0.5 };
const viewportOnce = { once: true };

const cardFadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.08 } }),
};

function ReviewCard({ review, index }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce}
      variants={cardFadeUp} custom={index} className="bg-white border border-black p-8 relative" data-testid={`review-card-${index}`}>
      <Quote className="w-8 h-8 text-yellow-400/30 absolute top-6 right-6" />
      <div className="flex gap-0.5 mb-4">
        {Array.from({ length: review.rating }).map((_, j) => (
          <Star key={`${review.id}-star-${j}`} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
        ))}
      </div>
      <p className="font-body text-sm text-zinc-600 leading-relaxed mb-6">{review.comment}</p>
      <p className="font-heading text-sm font-bold uppercase tracking-tight">{review.name}</p>
    </motion.div>
  );
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/reviews/public`);
        setReviews(res.data);
      } catch (err) {
        logger.error('Reviews fetch failed:', err);
      }
    };
    fetchReviews();
  }, []);

  if (!reviews.length) return null;

  return (
    <section data-testid="reviews-section" className="py-24 sm:py-32 px-6 bg-yellow-400 border-y-2 border-black">
      <div className="max-w-7xl mx-auto">
        <motion.p {...fadeIn} className="font-body text-xs uppercase tracking-[0.2em] text-black/40 mb-3">Customer Reviews</motion.p>
        <motion.h2 {...fadeInUp} transition={halfSec} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase mb-12 text-black">
          Real Feedback From Real Customers
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((r, i) => <ReviewCard key={r.id} review={r} index={i} />)}
        </div>
      </div>
    </section>
  );
}
